import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import initSelectDropdown from "./initSelectDropdown.js"; // Import the vanilla JS init function
/*
This is a re-usable select component, meant to be passed to the back end and used in cshtml. Before passing please note:

 - componentName - is important, it will update the ID's and labels so there are no duplicates if this component shows multiple
    times on one page. This will make it easier to pass to BE with the right IDs and labels
 - isScrollTo - This component was initially made for a scroll to selection, leave as false if it is a regular selection and ensure BE does not 
    place the js-select-scrollTo class in the cshtml
 - placeholder - this is optional. If placed, this will be the initial text shown in the input before selecting
 - options - either an array of strings, or array of objects with label and value
 - callback - optional function that fires when selection changes. When provided, uses React state management instead of vanilla JS
*/

const Select = props => {
  const {
    title,
    options,
    isScrollTo,
    isNavigateOnChange,
    componentName,
    placeholder,
    callback,
  } = props;
  const selectRef = useRef(null);
  const [selectedValue, setSelectedValue] = useState(null);

  // Determine React management immediately based on callback presence
  const [isReactManaged] = useState(
    props.isReactManaged || (callback && typeof callback === "function"),
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    options &&
      options.forEach((option, i) => {
        if (option.isDefault) {
          const value = option.value ?? option;
          const label = option.label ?? option;
          setSelectedValue({ value: value, label: label, index: i });
        }
      });
  }, []);

  // Initialize vanilla JS dropdown ONLY for non-React managed selects
  useEffect(() => {
    if (!isReactManaged) {
      initSelectDropdown();
    }
  }, [isReactManaged]);

  // Handle React-managed selection changes
  const handleOptionClick = (option, index, event) => {
    if (!isReactManaged) return; // Let vanilla JS handle it

    event.stopPropagation(); // Prevent event bubbling

    const value = option.value ?? option;
    const label = option.label ?? option;

    setSelectedValue({ value, label, index });
    setIsOpen(false); // Close dropdown after selection

    if (callback) {
      callback({
        value,
        label,
        index,
        option,
      });
    }
  };

  // Handle dropdown toggle for React-managed selects
  const handleDropdownToggle = () => {
    setIsOpen(!isOpen);
  };

  // Handle keyboard navigation for React-managed selects
  const handleKeyDown = event => {
    const { key } = event;

    if (key === "Enter" || key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (key === "Escape") {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isReactManaged) return;

    const handleClickOutside = event => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isReactManaged]);

  return (
    <div
      ref={selectRef}
      className={cn("custom-dropdown", {
        "js-select": !isReactManaged, // Only add vanilla JS class when not React-managed
        "js-select-scrollTo": isScrollTo && !isReactManaged,
        "js-select-navOnChange": isNavigateOnChange && !isReactManaged,
        "react-managed-select": isReactManaged,
        open: isReactManaged && isOpen, // Add open class for React-managed selects
      })}
    >
      <div className="sr-only" id={`${componentName}-dropdown-label`}>
        {title ?? "Table of Contents"}
      </div>
      <button
        className="custom-dropdown__input"
        id={`${componentName}-combo`}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isReactManaged ? isOpen : "false"}
        aria-labelledby={`${componentName}-dropdown-label`}
        aria-controls={`${componentName}-dropdown-list`}
        tabIndex="0"
        data-placeholder={placeholder}
        onClick={isReactManaged ? handleDropdownToggle : undefined}
        onKeyDown={isReactManaged ? handleKeyDown : undefined}
      >
        {isReactManaged && selectedValue ? selectedValue.label : placeholder}
      </button>
      <div
        className="custom-dropdown__menu"
        id={`${componentName}-dropdown-list`}
        role="listbox"
        tabIndex="-1"
      >
        {options &&
          options.map((option, i) => (
            <React.Fragment key={option.value ?? `option-${i}`}>
              <div
                id={`${componentName}-combo-${i}`}
                role="option"
                className={`custom-dropdown__option ${option.isActive ? "custom-dropdown__option--active" : ""}`}
                data-value={option.value ?? option}
                data-is-default={option.isDefault}
                onClick={
                  isReactManaged
                    ? event => handleOptionClick(option, i, event)
                    : undefined
                }
              >
                <span>{option.label ?? option}</span>
              </div>
              {option.children && option.children.length > 0 && (
                <div className="custom-dropdown__option-children">
                  {option.children.map((child, childIndex) => (
                    <div
                      key={child.value ?? child}
                      id={`${componentName}-dropdown-suboption-${i}-${childIndex}`}
                      role="option"
                      className="custom-dropdown__option"
                      tabIndex="-1"
                      data-value={child.value ?? child}
                      data-is-default={child.isDefault}
                      onClick={
                        isReactManaged
                          ? event =>
                              handleOptionClick(
                                child,
                                `${i}-${childIndex}`,
                                event,
                              )
                          : undefined
                      }
                    >
                      {child.label ?? child}
                    </div>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
      </div>
    </div>
  );
};
export default Select;
